// src/pages/Support/components/TicketTable.js

import React, { useState } from "react";
import { Table, Tag, Avatar, Button, Space, Tooltip, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  CheckCircleOutlined,
  UserOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import CustomLoaderIcon from "../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon";

const TicketTable = ({
  tickets,
  onMarkResolved,
  pagination,
  onPageChange,
  loading = false,
}) => {
  const navigate = useNavigate();
  const themeDefault = useSelector((state) => state.themeDefault);
  const primaryColor = themeDefault?.info?.colorPrimary || "#1890ff";

  // Get user initials for avatar
  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // Status badge configuration (matching BE statuses)
  const getStatusBadge = (status) => {
    const normalizedStatus = status?.toLowerCase();
    const config = {
      open: { color: "green", text: "OPENED" },
      opened: { color: "green", text: "OPENED" },
      new: { color: "blue", text: "NEW" },
      resolved: { color: "green", text: "RESOLVED" },
      closed: { color: "red", text: "CLOSED" },
      in_progress: { color: "cyan", text: "IN PROGRESS" },
      onhold: { color: "gold", text: "ONHOLD" },
      on_hold: { color: "gold", text: "ONHOLD" },
      hold: { color: "gold", text: "ONHOLD" },
    };
    const { color, text } = config[normalizedStatus] || {
      color: "default",
      text: status?.toUpperCase() || "UNKNOWN",
    };
    return <Tag color={color}>{text}</Tag>;
  };

  // Priority badge
  const getPriorityBadge = (priority) => {
    const config = {
      critical: { color: "red", text: "CRITICAL" },
      high: { color: "volcano", text: "HIGH" },
      medium: { color: "orange", text: "MEDIUM" },
      low: { color: "blue", text: "LOW" },
    };
    const { color, text } = config[priority] || {
      color: "default",
      text: priority,
    };
    return <Tag color={color}>{text}</Tag>;
  };

  // Navigate to detail page — encrypt the raw ObjectId so it is not
  // exposed in plain text in the browser address bar.
  const navigateToDetail = (record) => {
    const rawId = record._id || record.id;
    const encryptedId = btoa(rawId);
    navigate(`/admin/support-details/${encryptedId}`, {
      state: { ticketData: record },
    });
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return {
      time: date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      }),
      date: date.toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
    };
  };

  // Table columns definition (mapped to BE response structure)
  const columns = [
    {
      title: "TICKET ID",
      dataIndex: "ticketId",
      key: "ticketId",
      width: 80,
      render: (ticketId, record) => (
        <span
          onClick={() => navigateToDetail(record)}
          style={{
            fontFamily: "'Monaco', 'Menlo', monospace",
            fontWeight: 600,
            color: primaryColor,
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.target.style.textDecoration = "underline";
            e.target.style.opacity = "0.8";
          }}
          onMouseLeave={(e) => {
            e.target.style.textDecoration = "none";
            e.target.style.opacity = "1";
          }}>
          #{ticketId}
        </span>
      ),
    },
    {
      title: "SUBJECT",
      dataIndex: "subject",
      key: "subject",
      width: 80,
      // ellipsis: true,
      render: (subject) => (
        <span style={{ fontWeight: 500 }}>
          {subject || <span style={{ color: "#bfbfbf" }}>N/A</span>}
        </span>
      ),
    },
    {
      title: "STATUS",
      dataIndex: "status",
      key: "status",
      width: 100,
      align: "center",
      render: (status) => getStatusBadge(status),
    },
    {
      title: "CREATED BY",
      key: "createdBy",
      width: 100,
      render: (_, record) => {
        const userName = record.user?.name
          ? `${record.user.name.firstName || ""} ${record.user.name.lastName || ""}`.trim()
          : record.userId?.name || null;

        if (!userName) {
          return <span style={{ color: "#bfbfbf" }}>N/A</span>;
        }

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar
              size="small"
              src={record.user?.profileImage}
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                fontSize: 11,
              }}>
              {getInitials(userName)}
            </Avatar>
            <span style={{ fontSize: 13 }}>{userName}</span>
          </div>
        );
      },
    },

    {
      title: "CREATED DATE",
      dataIndex: "created_at",
      key: "created_at",
      width: 80,
      render: (date) => {
        const formatted = formatDateTime(date);
        if (!formatted.date) return <span style={{ color: "#bfbfbf" }}>N/A</span>;
        return (
          <div>
            <div>{formatted.date}</div>
            <div style={{ fontSize: 12, color: "#666" }}>{formatted.time}</div>
          </div>
        );
      },
    },
    {
      title: "LAST UPDATED",
      dataIndex: "lastMessageTime",
      key: "lastMessageTime",
      width: 80,
      render: (date) => {
        const formatted = formatDateTime(date);
        if (!formatted.date) return <span style={{ color: "#bfbfbf" }}>N/A</span>;
        return (
          <div>
            <div>{formatted.date}</div>
            <div style={{ fontSize: 12, color: "#666" }}>{formatted.time}</div>
          </div>
        );
      },
    },
    {
      title: "ACTIONS",
      key: "actions",
      width: 80,
      // fixed: "right",
      render: (_, record) => (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingRight: 8 }}>
            <div>
              {record.status !== "closed" ? (
                <Button
                  icon={<CheckCircleOutlined />}
                  onClick={() =>
                    onMarkResolved(
                      record._id,
                      record.subject,
                      "closed",
                      record?.ticketId,
                    )
                  }
                  style={{
                    fontWeight: 500,
                    borderRadius: 6,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}>
                  Mark as Closed
                </Button>
              ) : (
                getStatusBadge(record.status)
              )}
            </div>

            <Tooltip title="View Details">
              <ArrowRightOutlined
                onClick={() => navigateToDetail(record)}
                style={{
                  fontSize: 16,
                  color: primaryColor,
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "50%",
                  transition: "all 0.2s ease",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = themeDefault?.info?.newConfig?.colorPrimaryShades?.[0] || "rgba(24, 144, 255, 0.08)";
                  e.currentTarget.style.transform = "translateX(2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                  e.currentTarget.style.transform = "translateX(0)";
                }}
              />
            </Tooltip>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div
      style={{
        position: "relative",
        minHeight: "200px",
      }}>
      <Table
        columns={columns}
        dataSource={tickets}
        rowKey="_id"
        loading={{
          spinning: loading,
          indicator: (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}>
              <CustomLoaderIcon />
            </div>
          ),
        }}
        pagination={{
          current: pagination.current,
          total: pagination.total,
          pageSize: pagination.pageSize,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} tickets`,
          onChange: onPageChange,
          position: ["bottomCenter"],
          showSizeChanger: false,
        }}
        scroll={{ x: "auto" }}
        size="middle"
        onRow={(record) => ({
          onClick: (e) => {
            // Skip navigation when clicking inside a button or the arrow icon
            if (e.target.closest("button") || e.target.closest(".ant-btn")) return;
            navigateToDetail(record);
          },
          style: { cursor: "pointer" },
        })}
      />
    </div>
  );
};

export default TicketTable;
